import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EntradaCrearOpcion,
  EntradaEditarOpcion,
  OpcionCatalogo,
  PermisoInfo,
  Rol,
  TipoCatalogo,
} from '@copathy/shared';
import { api } from './api';
import { useRefrescarSesion } from './auth';

// ---------------------------------------------------------------------------
// Catálogos
// ---------------------------------------------------------------------------

export function useCatalogo(
  tipo: TipoCatalogo,
  opciones: { empresaId?: number | undefined; activo?: 'si' | 'no' | 'todas'; habilitado?: boolean } = {},
) {
  const { empresaId, activo = 'si', habilitado = true } = opciones;
  const parametros = new URLSearchParams({ tipo, activo });
  if (empresaId !== undefined) parametros.set('empresaId', String(empresaId));

  return useQuery({
    queryKey: ['catalogo', tipo, empresaId, activo],
    queryFn: () => api.get<OpcionCatalogo[]>(`/api/catalogos?${parametros.toString()}`),
    enabled: habilitado,
    // Cambia poquísimo. Lo invalida quien lo edita.
    staleTime: 5 * 60_000,
  });
}

export function useGuardarOpcion() {
  const qc = useQueryClient();
  // Se invalida TODO el catálogo, no solo el tipo tocado: el combo de sector del
  // formulario de clientes y esta pantalla son la misma lista con otra clave.
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['catalogo'] });

  const crear = useMutation({
    mutationFn: (datos: EntradaCrearOpcion) => api.post<OpcionCatalogo>('/api/catalogos', datos),
    onSuccess: invalidar,
  });

  const editar = useMutation({
    mutationFn: (v: { id: number; cambios: EntradaEditarOpcion }) =>
      api.patch<OpcionCatalogo>(`/api/catalogos/${v.id}`, v.cambios),
    onSuccess: invalidar,
  });

  return { crear, editar };
}

// ---------------------------------------------------------------------------
// Roles y permisos
// ---------------------------------------------------------------------------

export function useRoles(habilitado = true) {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<Rol[]>('/api/roles'),
    enabled: habilitado,
  });
}

export function usePermisos(habilitado = true) {
  return useQuery({
    queryKey: ['permisos'],
    queryFn: () => api.get<PermisoInfo[]>('/api/roles/permisos'),
    enabled: habilitado,
    staleTime: 10 * 60_000,
  });
}

export function useFijarPermisos() {
  const qc = useQueryClient();
  const refrescarSesion = useRefrescarSesion();

  return useMutation({
    mutationFn: (v: { rolId: number; permisos: string[] }) =>
      api.put<Rol>(`/api/roles/${v.rolId}/permisos`, { permisos: v.permisos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['roles'] });
      /*
        ⚠️ Y la sesión propia, que NO vive en TanStack Query sino en el contexto de
        auth. Si alguien edita los permisos de su propio rol, el menú y los botones
        seguirían mostrando lo de antes hasta cerrar sesión. La UI solo oculta —el
        servidor revalida siempre— pero una pantalla que ofrece lo que ya no se
        puede hacer se lee como una aplicación rota.
      */
      void refrescarSesion();
    },
  });
}
